import type { GetStaticPropsContext, NextPage } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { FC, useMemo, useState, useEffect } from 'react'
import { SwiperSlide } from 'swiper/react'
import useSWR, { SWRConfig } from 'swr'
import dayjs from 'dayjs'
import clsx from 'clsx'

import Testimonials, { TestimonialsProps } from '../components/customer-words-card/customer-words-card'
import PdFooter from '../components/pd-footer/pd-footer'
import PdImage from '../components/pd-image/pd-image'

import PdNavbar from '../components/pd-navbar/pd-navbar'
import PdNews from '../components/pd-news/pd-news'
import PdSection from '../components/pd-section/pd-section'
import PdSwiper, { Breakpoints, PdSwiperProps } from '../components/pd-swiper/pd-swiper'
import retriveMessages, { LocaleType } from '../messages/message'
import { getHomePageData, HomeData } from '../service/home.service'
import { withSEO } from '../common/with-seo'
import { SEOData } from '../service/seo.service'

import styles from '../styles/index.page.module.css'

/* Mock data */
import Link from 'next/link'
import { useImageColor } from '../hooks/use-image-color'
import AngleRight from '../components/pd-angle-right/pd-angle-right'
import PdMediaSwiper, { MediaItem } from '../components/pd-media-swiper/pd-media-swiper'
import { defaultRegionLocale, useChinaDetector, useGlobalDetector } from '../common/region-locale'
import AboutUsSection from '../components/about-us-section/about-us-section'
import Statement from '../components/pd-statement/pd-statement'
// import FlexBannerGroup, { FlexBannerGroupData } from '../components/pd-flex-banner-group/pd-flex-banner-group'
import PdGridBanner from '../components/pd-grid-banner/pd-grid-banner'
import { resolveMediaAlt } from '../common/media-alt'

interface BannerTopProps {
  img: string
  imageAlt?: string
  title: string
  jumpUrl: string
  bgImg: string
  priority?: boolean
  desc?: string
  fontColor?: string
}

function wrapImg(originImgSrc: string): string {
  const encodedOriginImgSrc = encodeURIComponent(originImgSrc)
  const localImgSrc = `/_next/image?url=${encodedOriginImgSrc}&w=1920&q=80`
  return localImgSrc
}

const BannerTop: FC<BannerTopProps> = ({ img, imageAlt, title, jumpUrl, bgImg, priority, desc, fontColor }) => {
  const t = useTranslations()
  const [color, setImgSrc] = useImageColor(wrapImg(bgImg), { sx: 960, sy: 0, sw: 1, sh: 1 })
  // 如果 fontColor 为空字符串，则使用 #fff
  const realFontColor = fontColor === '' ? '#ffffff' : fontColor

  let titleDom = null
  if (img && img !== '') {
    titleDom = (
      <div className={['w-full h-28px', 'laptop:w-full laptop:h-9'].join(' ')}>
        <PdImage
          src={img}
          enableMask={false}
          width={'100%'}
          height={'100%'}
          object="contain"
          alt={imageAlt ?? title}
          placeholder={'empty'}
          priority={priority}
        />
      </div>
    )
  } else {
    titleDom = (
      <h2
        style={{ color: realFontColor }}
        className={`text-2xl laptop:text-5xl text-center  italic font-bold w-[95vw] break-words`}
      >
        {title}
      </h2>
    )
  }

  return (
    <div
      className={[
        'pt-9 gap-2',
        'laptop:pt-12 laptop:gap-4',
        'absolute left-0 top-0 right-0 bottom-0 flex flex-col items-center pointer-events-none',
      ].join(' ')}
      style={{
        color: realFontColor,
      }}
    >
      {titleDom}
      <h3 style={{ color: realFontColor }} className={`text-sm laptop:text-xl/10  text-center w-[95vw] break-words`}>
        {desc}
      </h3>

      {(img || title || desc) && (
        <a href={jumpUrl} className="text-[0.6rem] laptop:text-sm flex items-center">
          {t('KnowMore')}
          <AngleRight
            size="4x8"
            customTheme={realFontColor}
            className="w-[6px] h-[10px]  laptop:w-[7px] laptop:h-[12px] ml-0.5 laptop:ml-1.5"
            pathClassName="ml-0"
          />
        </a>
      )}
    </div>
  )
}

const BannerTopText: FC<{ title: string; desc: string }> = ({ title, desc }) => {
  return (
    <div className="fixed left-[999999px]">
      <h1>{title}</h1>
      <h2>{desc}</h2>
      <noscript>{title}</noscript>
      <noscript>{desc}</noscript>
    </div>
  )
}

const Home: NextPage<{ fallback: any; homePageData: HomeData; seoData?: SEOData | null }> = ({
  fallback,
  homePageData,
  seoData,
}) => {
  return (
    <SWRConfig value={{ fallback }}>
      <HomeMain initData={homePageData} seoData={seoData} />
    </SWRConfig>
  )
}

const HomeMain: FC<{ initData: HomeData; seoData?: SEOData | null }> = ({ initData, seoData }) => {
  const t = useTranslations()
  const { locale } = useRouter()

  // 当用户和页面有交互了，才显示更多的内容
  const [showMore, setShowMore] = useState(false)
  const [baseFontSize, setBaseFontSize] = useState(16)

  const { data: _data, error } = useSWR<HomeData>(`${locale}/api/front/v1/home`, () =>
    getHomePageData(locale || defaultRegionLocale().locale)
  )
  const data: HomeData = _data || initData

  // const [isMobileDevice, setMobileDevice] = useState<boolean>(false)
  let isMobileDevice = false
  if (typeof window !== 'undefined' && window?.innerWidth < 1200 && !isMobileDevice) {
    isMobileDevice = true
  }

  useEffect(() => {
    setTimeout(() => {
      const cardEleList = document.querySelectorAll('.TestimonialsCard') as NodeListOf<HTMLDivElement>

      const maxHeight = Array.from(cardEleList).reduce<number>(
        (prev, curr) => (prev > curr.offsetHeight ? prev : curr.offsetHeight),
        0
      )

      Array.from(cardEleList).forEach((el) => {
        el.style.height = `${maxHeight}px`
      })
    }, 100)
  }, [data?.cards])

  // Lazy load component
  useEffect(() => {
    // 监听用户和页面交互了
    function handler() {
      if (!showMore) {
        setShowMore(true)
      }
    }

    const ListenEventTypes = ['keydown', 'pointerdown', 'click', 'scroll', 'resize']
    ListenEventTypes.forEach((eventType) => {
      window.addEventListener(eventType, handler, { capture: true, passive: true, once: true })
    })

    return () => {
      ListenEventTypes.forEach((eventType) => window.removeEventListener(eventType, handler))
    }
  }, [])

  /* 顶部的banner */
  const slides = useMemo(() => {
    if (data === undefined || !data.banner) {
      return []
    }
    return data.banner.map((b) => ({
      img: b.mediaUrl,
      mediaUrlH5: b.mediaUrlH5,
      captionTitle: b.title,
      captionDesc: b.desc,
      titleMediaUrl: b.titleMediaUrl,
      titleMediaAlt: resolveMediaAlt(b.titleMediaAlts, locale),
      jumpUrl: b.jumpUrl,
      fontColor: b.fontColor,
      alt: resolveMediaAlt(b.alts, locale),
      altH5: resolveMediaAlt(b.altsH5, locale),
    }))
  }, [data, locale])

  const sectionTitles = useMemo(() => {
    let messagesTitle = ''
    if (data && data.messages) {
      messagesTitle = data.messages?.title || ''
    }
    if (!data) {
      return {
        solutionTitle: t('Navbar.Solutions'),
        customerTitle: t('Navbar.CustomerCases'),
        cardTitle: t('Testimonial'),
        logoTitle: t('StrategicCooperation'),
        newsTitle: t('Navbar.News'),
        messagesTitle: messagesTitle,
      }
    }

    return {
      solutionTitle: data?.solutionTitle || t('Navbar.Solutions'),
      customerTitle: data?.customerTitle || t('Navbar.CustomerCases'),
      cardTitle: data?.cardTitle || t('Testimonial'),
      logoTitle: data?.logoTitle || t('StrategicCooperation'),
      newsTitle: data?.newsTitle || t('Navbar.News'),
      messagesTitle: messagesTitle,
    }
  }, [data])

  /* 产品线 - 已注释，使用 homeProductLines 替代 */
  // const productLines = useMemo<FlexBannerGroupData>(() => {
  //   if (!data?.productLine) {
  //     return []
  //   }

  //   return data.productLine.map((line) => {
  //     return {
  //       data: line.items.map((item) => ({
  //         img: item.mediaUrl,
  //         alt: item.title,
  //         jumpUrl: item.jumpUrl,
  //         head1: line.title,
  //         head2: item.title,
  //         head3: item.desc,
  //       })),
  //     }
  //   })
  // }, [data.productLine])

  /* 首页产品线 */
  const homeProductLines = useMemo(() => {
    if (!data?.homeProductLines) {
      return []
    }
    return data.homeProductLines.map((item) => ({
      title: item.title,
      mediaUrl: item.mediaUrl,
      h5Src: item.mediaH5Url,
      jumpUrl: item.jumpUrl,
      fontColor: item.fontColor,
      alt: resolveMediaAlt(item.alts, locale),
      altH5: resolveMediaAlt(item.altsH5, locale),
    }))
  }, [data?.homeProductLines, locale])

  /* 解决方案 */
  const sceneImages = useMemo(() => {
    if (!data || !data.solutions) {
      return []
    }

    return data.solutions.map((solution) => ({
      img: solution.mediaUrl,
      caption: solution.title,
      alt: resolveMediaAlt(solution.alts, locale),
      link: solution.jumpUrl || `/solutions/${encodeURIComponent(solution.title)}`,
    }))
  }, [data, locale])

  const sceneBreakpoints: Breakpoints = {
    640: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
    768: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
    1024: {
      slidesPerView: 'auto',
      spaceBetween: 16,
    },
  }

  /* 客户案例 */
  const cases = useMemo(() => {
    if (!data || !data.customers) {
      return []
    }

    return data.customers.map((c) => ({
      img: c.mediaUrl,
      caption: c.title,
      link: c.jumpUrl,
    }))
  }, [data])

  const mediaCases = useMemo<Array<MediaItem>>(() => {
    if (!data || !data.customers) {
      return []
    }

    const cases: Array<MediaItem> = data.customers.map((c) => ({
      src: c.mediaUrl,
      alt: resolveMediaAlt(c.alts, locale),
      type: c.mediaUrl.includes('.mp4') ? 'VIDEO' : 'IMG',
      caption: c.title,
      link: c.jumpUrl.replace(/[\u200B-\u200D\uFEFF]/g, ''),
    }))

    return cases
  }, [data, locale])

  const casesBreakpoints: Breakpoints = {
    640: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
    768: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
    1024: {
      slidesPerView: 'auto',
      spaceBetween: 16,
    },
  }

  /* 合作企业/客户的logo */
  const customerLogos = useMemo(() => {
    if (data === void 0) {
      return []
    }

    return data.customerLogos || []
  }, [data])

  const brandLogoBreakpoints = {
    640: {
      slidesPerView: 3,
      spaceBetween: 8,
      grid: { rows: 6 },
    },
    1024: {
      slidesPerView: 6,
      spaceBetween: 16,
      grid: { rows: 3 },
    },
  }

  /* 新闻咨询 */
  const newsList = useMemo(() => {
    if (!data || !data.topNews) {
      return []
    }

    return data.topNews.map((news) => ({
      cover: news.mediaUrl,
      imageAlt: resolveMediaAlt(news.alts, locale),
      title: news.title,
      overview: news.content,
      link: news.jumpUrl,
      date: dayjs(news.date)
        .locale(locale || '')
        .format('YYYY.MM.DD'),
      type: news.type,
    }))
  }, [data, locale])

  const miniNewsBreakpoints = {
    640: {
      slidesPerView: 2,
      spaceBetween: 8,
    },
    1024: {
      slidesPerView: 4,
      spaceBetween: 16,
    },
  }

  const pdSwiperProps = useMemo<PdSwiperProps>(() => {
    let props: PdSwiperProps = {}

    if (isMobileDevice || sceneImages.length > 5) {
      props = {
        slideToClickedSlide: false,
        enabled: true,
        slidesPerView: 'auto',
        spaceBetween: 8,
        autoplay: true,
        loop: true,
        breakpoints: sceneBreakpoints,
        hideNavigation: isMobileDevice,
        hidePagination: true,
        // paginationOptions: {
        //   el: '.pd-pagination-container_black-solutions',
        // },
        slidesOffsetBeforeProvider: () => (isMobileDevice ? 1.5 * baseFontSize : 1 * baseFontSize),
        slidesOffsetAfterProvider: () => (isMobileDevice ? 1.5 * baseFontSize : 1 * baseFontSize),
      }
    } else {
      props = {
        enabled: false,
        slideToClickedSlide: false,
        className: clsx('laptop:!px-4 *:gap-4'),
        hideNavigation: true,
      }
    }
    return props
  }, [sceneImages, isMobileDevice, baseFontSize, sceneBreakpoints])

  const miniNewsList = useMemo(() => {
    if (!data || !data.news) {
      return []
    }

    return data.news.map((news) => ({
      mini: true,
      cover: news.mediaUrl,
      imageAlt: resolveMediaAlt(news.alts, locale),
      title: news.title,
      overview: news.content,
      link: news.jumpUrl,
      date: dayjs(news.date)
        .locale(locale || '')
        .format('YYYY.MM.DD'),
      type: news.type,
    }))
  }, [data, locale])

  const customerWords: TestimonialsProps[] = useMemo(() => {
    if (!data || !data.cards) {
      return []
    }

    return data.cards.map((card) => ({
      avator: card.mediaUrl,
      imageAlt: resolveMediaAlt(card.alts, locale),
      name: card.title,
      jobTitle: card.secondTitle,
      words: card.content,
    }))
  }, [data, locale])

  /* 指标数据 */
  const messages = useMemo(() => {
    if (!data || !data.messages) {
      return {
        title: '',
        content: '',
        mediaUrl: '',
        alts: null,
        item: [],
      }
    }

    return data.messages
  }, [data])

  useEffect(() => {
    const baseFontSize = parseInt(document.documentElement.style.fontSize || '16px')
    setBaseFontSize(baseFontSize)
  }, [])

  // SEO 逻辑已由 _app.tsx 统一处理，这里不再需要 PdSEO
  // 只需要将 seoData 传递给 _app.tsx 即可（已经通过 getStaticProps 完成）

  return (
    <div className="container-fluid w-full h-full">
      {/* PdSEO removed - handled globally */}

      {/* Google Tag Manager (noscript)  */}
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-PJD8SH3"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        ></iframe>
      </noscript>
      {/* End Google Tag Manager (noscript)  */}

      <main className="container-fluid flex flex-col justify-center w-full h-full">
        <PdNavbar sticky={true} />

        <div className={['h-144', 'laptop:h-200', 'w-full relative'].join(' ')}>
          {slides.length > 0 && (
            <PdSwiper
              effect="fade"
              slidesPerView={1}
              spaceBetween={8}
              autoplay={true}
              loop={true}
              paginationOptions={{
                el: '.pd-pagination-container-banner',
              }}
              hideNavigation={isMobileDevice}
            >
              {slides.map((slide, index) => (
                <SwiperSlide key={[slide.img, index].join('-')} className="relative cursor-pointer">
                  <PdImage
                    src={slide.img}
                    h5Src={slide.mediaUrlH5}
                    enableMask={false}
                    width={'100%'}
                    height="100%"
                    alt={(isMobileDevice ? slide.altH5 : slide.alt) ?? slide.captionDesc}
                    linkHref={slide.jumpUrl}
                    useLoader={true}
                    priority={index < 2}
                  >
                    <BannerTopText title={slide.captionTitle} desc={slide.captionDesc} />
                    <BannerTop
                      img={slide.titleMediaUrl}
                      imageAlt={slide.titleMediaAlt}
                      title={slide.captionTitle}
                      desc={slide.captionDesc}
                      jumpUrl={slide.jumpUrl}
                      fontColor={slide.fontColor}
                      bgImg={slide.img}
                      priority={index < 2}
                    />
                  </PdImage>
                </SwiperSlide>
              ))}
            </PdSwiper>
          )}
          <div className="absolute left-0 !bottom-6 z-10 pd-pagination-container-banner"></div>
        </div>

        <div
          className={[
            'gap-2 flex-col mt-2',
            'laptop:px-4 laptop:gap-4 laptop:flex-row laptop:mt-4',
            'flex relative',
          ].join(' ')}
        >
          {/* 原 FlexBannerGroup 已注释，使用 PdGridBanner 替代 */}
          {/* <FlexBannerGroup groups={productLines} /> */}
          <PdGridBanner data={homeProductLines} />
        </div>

        {/* 解决方案 */}
        <PdSection title={sectionTitles.solutionTitle} bgWhite={true}>
          <div className="w-full pl-0 relative h-fit laptop:h-fit">
            {sceneImages.length > 0 && (
              <PdSwiper {...pdSwiperProps}>
                {sceneImages?.map((item, index) => (
                  <SwiperSlide
                    key={item.img + index}
                    className={clsx(
                      'cursor-pointer [aspect-ratio:1/1]',
                      '!w-75',
                      sceneImages.length < 6 ? 'laptop:!w-auto laptop:flex-1' : 'laptop:!w-372px'
                    )}
                  >
                    <PdImage
                      src={item.img}
                      alt={item.alt}
                      width="100%"
                      height="100%"
                      enableMask={true}
                      object="cover"
                      enableHoverScale={true}
                      linkHref={item.link}
                    >
                      <div className="absolute left-8 bottom-8 text-white w-[calc(100%-18vw)] laptop:w-[calc(100%-5vw)]">
                        <h3
                          className={[
                            'w-full text-2xl pb-2 text-white break-words',
                            'laptop:text-26 laptop:pb-2',
                            'font-semibold',
                          ].join(' ')}
                        >
                          {item.caption}
                        </h3>
                        <a className="text-base flex items-center" href={item.link}>
                          {t('KnowMore')}
                          <AngleRight theme="WHITE" size="4x8" />
                        </a>
                      </div>
                    </PdImage>
                  </SwiperSlide>
                ))}
              </PdSwiper>
            )}
            <div className="absolute left-0 !-bottom-6 z-10 pd-pagination-container_black-solutions"></div>
            <div className="absolute left-1/2 -translate-x-1/2 laptop:!-bottom-16 !-bottom-14">
              <Link href="/solutions">
                <span className="laptop:text-base text-sm flex items-center text-pdblue cursor-pointer">
                  {t('KnowMore')}
                  <AngleRight theme="PUDUBLUE" size="4x8" />
                </span>
              </Link>
            </div>
          </div>
        </PdSection>

        {/* Successful stories pro */}
        {showMore && (
          <PdSection title={sectionTitles.customerTitle}>
            <div className={['w-full h-80', 'laptop:h-175', 'relative'].join(' ')}>
              <PdMediaSwiper
                slideClassName="laptop:!w-1/2"
                data={mediaCases}
                slideChildren={(props) => {
                  return (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-76px text-white text-center">
                      <h3
                        className={[
                          'text-white',
                          'text-2xl pb-2 px-4',
                          'laptop:text-3xl laptop:pb-2',
                          'font-semibold',
                        ].join(' ')}
                      >
                        {props.caption}
                      </h3>
                      <Link href={props.link || '#'}>
                        <span className="cursor-pointer text-base">
                          {t('KnowMore')}
                          <AngleRight size="4x8" />
                        </span>
                      </Link>
                    </div>
                  )
                }}
              ></PdMediaSwiper>

              <div className="absolute left-1/2 -translate-x-1/2 laptop:!-bottom-16 !-bottom-14">
                <Link href="/successful-stories">
                  <span className="laptop:text-base text-sm flex items-center text-pdblue cursor-pointer">
                    {t('KnowMore')}
                    <AngleRight theme="PUDUBLUE" size="4x8" />
                  </span>
                </Link>
              </div>
            </div>
          </PdSection>
        )}

        {/* Testimonial */}
        {showMore && customerWords.length > 0 && typeof window !== 'undefined' && (
          <PdSection title={sectionTitles.cardTitle}>
            <div className={[customerWords?.length <= 0 ? 'hidden' : '', 'w-full'].join(' ')}>
              {customerWords.length > 0 && (
                <PdSwiper
                  slidesPerView={'auto'}
                  spaceBetween={16}
                  breakpoints={{
                    640: { slidesPerView: 'auto', spaceBetween: 16 },
                    768: { slidesPerView: 'auto', spaceBetween: 16 },
                    1024: { slidesPerView: 'auto', spaceBetween: 16 },
                    1280: { slidesPerView: 'auto', spaceBetween: 16 },
                  }}
                  hideNavigation={true}
                  hidePagination={true}
                  autoplay={true}
                  center={true}
                  loop={true}
                >
                  {customerWords?.map((cw, index) => (
                    <SwiperSlide key={cw.name + index} className="!w-fit !h-auto">
                      <Testimonials
                        avator={cw.avator}
                        imageAlt={cw.imageAlt}
                        name={cw.name}
                        jobTitle={cw.jobTitle}
                        words={cw.words}
                        className="h-full"
                      />
                    </SwiperSlide>
                  ))}
                </PdSwiper>
              )}
            </div>
          </PdSection>
        )}

        {/* About us section, Why Pudu Robotics */}
        {showMore && (
          <AboutUsSection
            title={sectionTitles.messagesTitle}
            mediaUrl={messages.mediaUrl}
            imageAlt={resolveMediaAlt(messages.alts, locale)}
            desc={messages.content}
            indicatorData={messages.item.map((v) => ({ label: v.name, value: v.desc }))}
          />
        )}

        {/* 与众多知名品牌达成战略合作 */}
        {showMore && (
          <PdSection title={sectionTitles.logoTitle}>
            <div
              className={[
                'h-67 w-[calc(100%-3rem)] mx-auto',
                'laptop:w-full laptop:max-w-300 laptop:h-242px',
                'relative',
              ].join(' ')}
            >
              {customerLogos.length > 0 && (
                <PdSwiper
                  slidesPerView={3}
                  spaceBetween={8}
                  grid={{ rows: 6 }}
                  autoplay={true}
                  breakpoints={brandLogoBreakpoints}
                  paginationOptions={{
                    el: '.pd-pagination-container_black-sc',
                  }}
                  hideNavigation={true}
                >
                  {customerLogos.map((img, index) => (
                    <SwiperSlide
                      key={img + index}
                      className={['!h-9', 'laptop:!h-3r laptop:p-0', 'flex bg-white'].join(' ')}
                    >
                      <PdImage src={img} width="100%" height="100%" object="contain" enableMask={false} />
                    </SwiperSlide>
                  ))}
                </PdSwiper>
              )}
              <div className="absolute left-0 !-bottom-28px z-10 pd-pagination-container_black-sc"></div>
            </div>
          </PdSection>
        )}

        {/*新闻资讯*/}
        {showMore && (
          <PdSection title={sectionTitles.newsTitle}>
            <div className={['w-full max-w-300', 'laptop:h-110 laptop:bg-white'].join(' ')}>
              {!!newsList?.length ? (
                <PdSwiper
                  effect="fade"
                  fadeEffect={{ crossFade: true }}
                  loop={true}
                  slidesPerView={1}
                  spaceBetween={16}
                  autoplay={true}
                  navgationOptions={
                    isMobileDevice
                      ? {
                          navigationEl: 'pd-navigation h5-new-navigation',
                          nextEl: 'pd-navigation-next h5-new-navigation-next',
                          prevEl: 'pd-navigation-prev h5-new-navigation-prev',
                        }
                      : {
                          navigationEl: 'pd-navigation news-navigation',
                          nextEl: 'pd-navigation-next news-navigation-next',
                          prevEl: 'pd-navigation-prev news-navigation-prev',
                        }
                  }
                >
                  {newsList?.map((news, index) => (
                    <SwiperSlide key={news.title + index}>
                      <div className={['p-0', 'laptop:p-8 laptop:h-full'].join(' ')}>
                        <PdNews
                          img={news.cover}
                          imageAlt={news.imageAlt}
                          title={news.title}
                          overview={news.overview}
                          date={news.date}
                          link={news.link}
                          type={news.type}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </PdSwiper>
              ) : null}
            </div>

            <div className="w-full max-w-300 laptop:h-76 px-6 laptop:px-0 mt-4">
              {miniNewsList.length > 0 && (
                <PdSwiper
                  slidesPerView={1}
                  spaceBetween={16}
                  breakpoints={miniNewsBreakpoints}
                  hidePagination={true}
                  navgationOptions={
                    isMobileDevice
                      ? {
                          navigationEl: 'pd-navigation h5-new-navigation',
                          nextEl: 'pd-navigation-next h5-news-navigation-next',
                          prevEl: 'pd-navigation-prev h5-news-navigation-prev',
                        }
                      : {
                          navigationEl: 'pd-navigation h5-new-navigation',
                          nextEl: 'pd-navigation-next h5-news-navigation-next',
                          prevEl: 'pd-navigation-prev h5-news-navigation-prev',
                        }
                  }
                >
                  {miniNewsList?.map((news, index) => (
                    <SwiperSlide key={news.title + index}>
                      <PdNews
                        mini={news.mini}
                        img={news.cover}
                        imageAlt={news.imageAlt}
                        title={news.title}
                        overview={news.overview}
                        date={news.date}
                        link={news.link}
                        type={news.type}
                      />
                    </SwiperSlide>
                  ))}
                </PdSwiper>
              )}
            </div>
          </PdSection>
        )}

        {showMore && data?.homeNews && (
          <Statement
            title={data?.homeNews?.title ?? ''}
            content={data?.homeNews?.content ?? ''}
            jumpUrl={data?.homeNews?.jumpUrl ?? ''}
            image={data?.homeNews?.mediaUrl ?? ''}
            imageAlt={resolveMediaAlt(data.homeNews.alts, locale)}
          />
        )}
      </main>

      <footer>
        <PdFooter />
      </footer>
    </div>
  )
}

export default Home

// 创建默认的空首页数据结构，避免构建失败
const createEmptyHomeData = (): HomeData => ({
  banner: [],
  productLine: [],
  homeProductLines: [],
  solutions: [],
  customers: [],
  customerLogos: [],
  cards: [],
  messages: {
    title: '',
    content: '',
    mediaUrl: '',
    alts: null,
    item: [],
  },
  topNews: [],
  news: [],
  homeNews: {
    docPath: '',
    jumpUrl: '',
    mediaUrl: '',
    alts: null,
    title: '',
    content: '',
    date: '',
  },
  solutionTitle: '',
  customerTitle: '',
  cardTitle: '',
  logoTitle: '',
  newsTitle: '',
})

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const rl = defaultRegionLocale()
    const messages = retriveMessages(['index'], locale as LocaleType)

    try {
      const homePageData = await getHomePageData(locale || rl.locale)

      // 验证数据完整性
      if (!homePageData) {
        console.warn(`Home page data missing for locale ${locale}`)
        return {
          props: {
            messages,
            homePageData: createEmptyHomeData(),
            fallback: false,
          },
          revalidate: 60,
        }
      }

      return {
        props: {
          messages,
          homePageData,
          fallback: false,
        },
        revalidate: 60,
      }
    } catch (error) {
      console.error(`Error fetching home page data for locale ${locale}:`, error)
      return {
        props: {
          messages,
          homePageData: createEmptyHomeData(),
          fallback: false,
        },
        revalidate: 60,
      }
    }
  },
  () => '/' // 首页路径
)
